// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

import { useEffect, useState } from 'react';

type VersionCheckResponse = {
  status: 'ok' | 'update_recommended' | 'update_required';
  minimum_version: string;
  recommended_version: string;
  update_url: string;
};

type VersionCheckProps = {
  appVersion: string;
  gracePeriodMs?: number; // default 24 hours
};

const DISMISS_KEY = 'version_check_dismissed_at';
const DEFAULT_GRACE_PERIOD = 24 * 60 * 60 * 1000; // 24 hours

export default function VersionCheck({
  appVersion,
  gracePeriodMs = DEFAULT_GRACE_PERIOD,
}: VersionCheckProps) {
  const [versionInfo, setVersionInfo] = useState<VersionCheckResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    fetch(`${baseUrl}/version/check?version=${encodeURIComponent(appVersion)}&platform=web`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: VersionCheckResponse) => setVersionInfo(data))
      .catch(() => {
        // Silently fail — version check is non-critical
      });
  }, [appVersion]);

  if (!versionInfo) return null;
  if (versionInfo.status === 'ok') return null;

  // For soft updates, respect dismissal grace period
  if (versionInfo.status === 'update_recommended') {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && !dismissed) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < gracePeriodMs) {
        return null;
      }
    }
    if (dismissed) return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  if (versionInfo.status === 'update_required') {
    return (
      <div
        data-testid="version-prompt"
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80"
      >
        <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl">
          <h2 className="mb-2 text-lg font-semibold text-slate-900">Update Required</h2>
          <p className="mb-4 text-sm text-slate-600">
            Your app version ({appVersion}) is no longer supported. Please update to version{' '}
            {versionInfo.minimum_version} or later to continue using ClubView.
          </p>
          {versionInfo.update_url ? (
            <a
              href={versionInfo.update_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Update Now
            </a>
          ) : (
            <p className="text-sm text-slate-500">
              Please refresh this page to get the latest version.
            </p>
          )}
        </div>
      </div>
    );
  }

  // update_recommended
  return (
    <div
      data-testid="version-prompt"
      className="fixed bottom-4 right-4 z-40 max-w-sm rounded-lg border border-brand-200 bg-white p-4 shadow-lg"
    >
      <h3 className="mb-1 text-sm font-semibold text-slate-900">Update Available</h3>
      <p className="mb-3 text-xs text-slate-600">
        A new version ({versionInfo.recommended_version}) is available. You are on {appVersion}.
      </p>
      <div className="flex gap-2">
        {versionInfo.update_url && (
          <a
            href={versionInfo.update_url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
          >
            Update Now
          </a>
        )}
        <button
          onClick={handleDismiss}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          Later
        </button>
      </div>
    </div>
  );
}
